import { Formik } from "formik";
import * as yup from "yup";
import { Input } from "@repo/ui/components/Base/Input";
import { Button } from "@repo/ui/components/Base/Button";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";
import type { UserWRole } from "@repo/types";
import { apiService } from "@/services/apiService";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { userRepo } from "@/repositories/userRepo";
import { tenantRepo } from "@/repositories/tenantRepo";
import { CheckboxInput } from "@repo/ui/components/Base/CheckboxInput";
import { useUserList } from "@/hooks/queryHooks/userHooks";

export interface CreateUserFormProps {
  tenantId: string;
  handleCancel: () => void;
  handleComplete: (user: UserWRole) => void;
  withinDialog?: boolean;
}

export const CreateUserForm = ({
  tenantId,
  handleCancel,
  handleComplete,
  withinDialog,
}: CreateUserFormProps) => {
  useBlockUnload();

  const { data: users } = useUserList(tenantId);

  const validationSchema = yup.object({
    email: yup
      .string()
      .email("Enter a valid email")
      .notOneOf(
        users?.map((user) => user.email) ?? [],
        "Email is already in tenant"
      )
      .required("Email is required"),
  });

  return (
    <Formik
      initialValues={{ email: "", sendInvitation: true }}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        if (!tenantId) {
          console.log("No tenant ID", tenantId);
          return;
        }

        const user = await userRepo.create(values);
        await tenantRepo.addUser(tenantId, user.id, "view");

        if (values.sendInvitation) {
          apiService.v1.mailNewUser({ tenantId, email: values.email }); // run separately; await not needed
        }
        handleComplete({ ...user, role: "view" });
        resetForm();
      }}
    >
      {({
        values,
        setFieldValue,
        handleSubmit,
        handleBlur,
        handleChange,
        resetForm,
        errors,
        touched,
        isSubmitting,
      }) => {
        const footer = (
          <>
            <Button
              variant="destructive"
              type="button"
              disabled={isSubmitting}
              onClick={() => {
                resetForm();
                handleCancel();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              Add
            </Button>
          </>
        );

        const form = (
          <div className="flex flex-col gap-y-4">
            <Input
              label="*Email Address"
              id="email"
              name="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.email && touched.email}
              errorMessage={errors.email}
            />
            <CheckboxInput
              orientation="horizontal"
              checked={values.sendInvitation}
              onCheckedChange={(checked) =>
                setFieldValue("sendInvitation", !!checked)
              }
              label="Send Invitation Email"
            />
            {values.sendInvitation && (
              <p>
                The new user you are adding will receive an email inviting them
                to join CiviDocs. There is a link in that email that they will
                have to click. When they see the login screen, they will have to
                sign up and set their password. If they do not receive this
                email, have them check their SPAM folder, or ask your email
                administrator if the email was blocked. If so, have them
                whitelist docs@civi.link.
              </p>
            )}

            {!withinDialog && (
              <div className="flex flex-row justify-between">{footer}</div>
            )}
          </div>
        );

        return (
          <form onSubmit={handleSubmit}>
            {withinDialog ? (
              <>
                <DialogBody>{form}</DialogBody>
                <DialogFooter className="justify-between">
                  {footer}
                </DialogFooter>
              </>
            ) : (
              form
            )}
          </form>
        );
      }}
    </Formik>
  );
};
